/**
 * useWorkflowServiceOrderSources - Fontes da ação "Criar ordem de serviço" do Workflow
 *
 * Reaproveita as RPCs oficiais do módulo O.S. (tipos de serviço, fases e pessoas
 * com papel de prestador). Só consulta quando o processo é de Negócio e o módulo
 * O.S. está ligado no tenant, evitando chamadas desnecessárias no designer.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-17 v1.0.1 - Corrige listas vazias: módulo O.S. lido por useServiceOrderModuleEnabled.
 *   RELEASE: 2026-10-17 v1.0.0 - Versão inicial (ação service_order.create).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';

const logger = createLogger('useWorkflowServiceOrderSources');

/** Opção simples de seletor (mesmo formato de WorkflowParamOption) */
interface Opcao { value: string; label: string; grupo?: string }

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export function useWorkflowServiceOrderSources(entidade?: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  // Só faz sentido em processos de Negócio com o módulo O.S. ligado
  // v1.0.1: leitura oficial do módulo (o tenant ativo não carrega essa flag)
  const moduloOs = useServiceOrderModuleEnabled();
  const ativo = !!tenantId && entidade === 'crm_deal' && moduloOs;

  const { data } = useQuery({
    queryKey: ['workflow-os-sources', tenantId],
    enabled: ativo,
    staleTime: 60_000,
    queryFn: async () => {
      const [tipos, fases, prestadores] = await Promise.all([
        rpc('get_service_types', { p_only_active: true, p_tenant_hint: tenantId }),
        rpc('get_service_order_phases', { p_category_id: null, p_tenant_hint: tenantId }),
        rpc('get_persons', { p_search: null, p_role: 'service_provider', p_status: 'active', p_page: 1, p_page_size: 100, p_tenant_hint: tenantId }),
      ]);
      for (const r of [tipos, fases, prestadores]) {
        if (r.error) logger.error('Falha ao carregar fontes da ação Criar O.S.', { error: r.error.message });
      }
      return {
        service_types: ((tipos.data as any[]) ?? []).map((t) => ({ value: t.id, label: t.name })) as Opcao[],
        service_order_phases: ((fases.data as any[]) ?? [])
          .filter((p) => p.is_active)
          .map((p) => ({ value: p.id, label: p.name, grupo: p.category_name ?? undefined })) as Opcao[],
        // Categorias derivadas das fases (mesma RPC, sem consulta extra)
        service_order_categories: Array.from(new Map(((fases.data as any[]) ?? [])
          .filter((p) => p.category_id)
          .map((p) => [p.category_id, { value: p.category_id, label: p.category_name ?? '' }])).values()) as Opcao[],
        service_providers: (((prestadores.data as any)?.items as any[]) ?? []).map((p) => ({ value: p.id, label: p.name })) as Opcao[],
      };
    },
  });

  return {
    service_types: data?.service_types ?? [],
    service_order_phases: data?.service_order_phases ?? [],
    service_order_categories: data?.service_order_categories ?? [],
    service_providers: data?.service_providers ?? [],
  };
}
